import React, { type FC, useCallback, useMemo } from "react";
import "./StockPlacementCard.scss";
import { useActionSheet, useWindows } from "@app/overlay";
import { useBoAvOperations, type ApiOmpBoAvOperation } from "@src/api/bo";
import { CardHeader } from "@shared/ui/card-header";
import { CardLoader } from "@src/components/card-loader";
import { useRequiredNumberParam } from "@src/hooks/useRequiredNumberParam";
import {
    BusinessBoStateValue,
    BusinessDateValue,
    BusinessDivisionValue,
    formatBoStateText,
    formatDivisionText,
} from "@src/components/business-data-display";
import type { ApiStockOrderSupplyDoc } from "../../../../api/stockOrders";
import type { ActionSheetSection } from "@shared/ui/overlay/action-sheet";
import { DetailList } from "@shared/ui/detail-row";
import { OverflowMarquee } from "@src/shared/ui";
import { formatStockOrderSupplierText, StockOrderSupplierValue } from "../../helpers/resolveStockOrderSupplier";
import { StockPlacementContentsList } from "./components/StockPlacementContentsList/StockPlacementContentsList";
import { StockPlacementScanPlaceholder } from "./components/StockPlacementScanPlaceholder/StockPlacementScanPlaceholder";
import { useStockPlacementCardQuery } from "./hooks/useStockPlacementCardQuery";
import { useStockPlacementContentsQuery } from "./hooks/useStockPlacementContentsQuery";
import { useBoOperationActionItems } from "@src/businessModules/WorkShop/shared/bo/useBoOperationActionItems";
import { useBoOperationConfirm } from "@src/businessModules/WorkShop/shared/bo/useBoOperationConfirm";
import { useStockPlacementCardMutations } from "./useStockPlacementCardMutations";
import { useStockPlacementCancelConfirm } from "./useStockPlacementCancelConfirm";
import { stockPlacementCardWindows } from "./stockPlacementCard.windows";

/** Карточка приходного ордера для размещения ТМЦ. */
export const StockPlacementCard: FC = () => {
    const orderCode = useRequiredNumberParam("code", "код приходного ордера");
    const windows = useWindows();
    const actionSheet = useActionSheet();

    const cardQuery = useStockPlacementCardQuery(orderCode);
    const contentsQuery = useStockPlacementContentsQuery(orderCode);
    const order = cardQuery.data;
    const avOperationsQuery = useBoAvOperations(order?.Code, order != null);

    const mutations = useStockPlacementCardMutations({
        onContentRefetch: (contentCode) => contentsQuery.refetchContentByCode(contentCode),
        onBoOperationSuccess: () => {
            void cardQuery.refetch();
            void contentsQuery.refetch();
            void avOperationsQuery.refetch();
        },
    });

    const { confirmAndCancel } = useStockPlacementCancelConfirm({
        perform: mutations.cancelPlacementMutation.mutateAsync,
    });

    const { confirmAndPerform } = useBoOperationConfirm({
        perform: mutations.performBoOperationMutation.mutateAsync,
    });

    const buildBoOperationParams = useCallback((operation: ApiOmpBoAvOperation, currentOrder: NonNullable<typeof order>) => {
        return {
            operation,
            boCode: currentOrder.Code,
            documentLabel: `Приходный ордер №${currentOrder.Num}`,
            fields: [
                { label: "Поставщик", value: formatEmptyValue(formatStockOrderSupplierText(currentOrder)) },
                { label: "Склад", value: formatEmptyValue(formatDivisionText(currentOrder.Division)) },
                { label: "Статус", value: formatEmptyValue(formatBoStateText(currentOrder.State)) },
            ],
        };
    }, []);

    const operationItems = useBoOperationActionItems({
        operations: avOperationsQuery.data,
        context: order,
        confirmAndPerform,
        buildParams: buildBoOperationParams,
    });

    const actionSheetSections = useMemo<ActionSheetSection[]>(() => {
        if (operationItems.length === 0) {
            return [];
        }

        return [
            {
                id: "bo",
                items: [
                    {
                        type: "drill-in",
                        id: "bo-operations",
                        label: "Выполнить операцию",
                        description: "Выбрать доступную БО-операцию",
                        sections: [{ id: "bo-operations", items: operationItems }],
                    },
                ],
            },
        ];
    }, [operationItems]);

    const openContentWindow = useCallback(
        (contentCode: number): void => {
            if (order == null) {
                return;
            }

            stockPlacementCardWindows.content.open(windows, {
                orderCode,
                contentCode,
                divisionCode: order.DivisionCode,
                placeLotMutation: mutations.placeLotMutation,
                confirmAndCancelPlacement: confirmAndCancel,
                // TODO(overlay): isPending здесь — снимок при open; в окне не реактивен. См. prop JSDoc.
                isCancelPlacementPending: mutations.cancelPlacementMutation.isPending,
            });
        },
        [
            confirmAndCancel,
            mutations.cancelPlacementMutation.isPending,
            mutations.placeLotMutation,
            order,
            orderCode,
            windows,
        ],
    );

    if (cardQuery.isFetching || order == null) {
        return <CardLoader header list />;
    }

    return (
        <div className="stock_placement_card_page">
            <CardHeader
                text={`Приходный ордер №${order.Num}`}
                buttons={[
                    {
                        type: "icon",
                        text: "Действия",
                        srcIcon: "/images/common/more_vert.svg",
                        onClick: () => actionSheet.open({ title: "Действия", sections: actionSheetSections }),
                    },
                ]}
            />
            <DetailList className="stock_placement_card_inform" layout="tiles" columns="auto" minWidth={96} oneLine>
                <DetailList.Row label="Дата" value={<BusinessDateValue value={order.Date} />} />
                <DetailList.Row label="Поставщик" value={<StockOrderSupplierValue order={order} />} />
                <DetailList.Row label="Статус" value={<BusinessBoStateValue state={order.State} />} />
                <DetailList.Row label="Сопровод. док." value={formatSupplyDocs(order.SupplyDocs)} />
                <DetailList.Row label="Склад" value={<BusinessDivisionValue division={order.Division} />} />
            </DetailList>
            <StockPlacementContentsList contentsQuery={contentsQuery} onOpenContent={openContentWindow} />
            <StockPlacementScanPlaceholder
                orderCode={orderCode}
                contents={contentsQuery.content}
                enabled={!contentsQuery.isFetching}
                onOpenContent={openContentWindow}
            />
        </div>
    );
};

/**
 * Нормализует пустые подписи справочных значений для confirm-диалогов.
 * @param value отформатированное display-значение
 */
function formatEmptyValue(value: string): string {
    return value === "" ? "—" : value;
}

/**
 * Форматирует сопроводительные документы ПО для карточки.
 * @param docs документы из `supplyDocs.loadSupplyDocs`
 */
function formatSupplyDocs(docs: ApiStockOrderSupplyDoc[] | undefined): React.ReactNode {
    if (docs == null || docs.length === 0) {
        return "Не задано";
    }

    const text = docs
        .map((doc) => {
            const num = doc.DocNum.trim();
            return num === "" ? "—" : num;
        })
        .join(", ");

    return <OverflowMarquee>{text}</OverflowMarquee>;
}
